import { Box, Flex, GridItem } from '@chakra-ui/react'
import Image from 'next/image'
import DocumentToReactRender from '~/components/shared-components/DocumentToReactRender/DocumentToReactRender'
import { type IHeroBanner } from '~/types/generated/contentful'
import { getBackgroundImageSrc } from '~/utils/ContentfulHelpers/ContentfulHelpers'

import ContainerGrid from '../../grid/ContainerGrid/ContainerGrid'
import ContentfulButton from '../../section-modules/ContentfulButton/ContentfulButton'
import styles from './HeroBannerModule.module.css'

export default function HeroBannerModule({
  componentData,
}: {
  componentData: IHeroBanner
}) {
  const {
    richHeader,
    description,
    textOnTheRightSide,
    backgroundImage,
    mobileImage,
    button,
    textColour = true,
  } = componentData.fields

  const fontColor = textColour ? 'primary.white.100' : 'primary.teal.100'
  const bgColor = textColour ? 'primary.teal.100' : 'primary.teal.60'

  //TOO replace as={headerTag as React.ElementType} (@Sam-Morgan-Tyghe)

  return (
    <>
      <div className={styles['hero-banner-desktop-only']}>
        <Box
          className="hero-banner"
          bgImage={{
            base: `url(${getBackgroundImageSrc(backgroundImage, { height: 528 })})`,
          }}
          bgPosition={{
            base: 'bottom 80px center',
            md: 'center',
          }}
          bgRepeat="no-repeat"
          bgSize="cover"
          bgColor={{
            md: bgColor,
          }}
          as="section"
        >
          <Flex
            justifyContent={{
              base: 'flex-end',
              md: 'center',
            }}
            alignItems="center"
            flexDirection="column"
            textAlign="left"
            minH={{ base: 'max(calc(100vh - 60px), 528px)', md: '528px' }}
            pt={{
              base: '400px',
              md: 0,
            }}
          >
            <Flex
              flexDirection="column"
              gap="40px"
              w="100%"
              bgColor={{
                base: bgColor,
                md: 'transparent',
              }}
              borderTopLeftRadius={{
                base: '100% 80px',
                md: 0,
              }}
              borderTopRightRadius={{
                base: '100% 80px',
                md: 0,
              }}
              py="40px"
            >
              <ContainerGrid width="100%">
                <GridItem
                  colStart={{
                    base: 1,
                    md: textOnTheRightSide ? 6 : 1,
                  }}
                  colEnd={{ base: 13, md: textOnTheRightSide ? 13 : 8 }}
                >
                  <Flex
                    flexDirection="column"
                    w={{ base: '100%' }}
                    textAlign={{
                      base: 'center',
                      md: textOnTheRightSide ? 'right' : 'left',
                    }}
                    alignItems={{
                      base: 'center',
                      md: textOnTheRightSide ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {richHeader && (
                      <DocumentToReactRender
                        componentData={richHeader}
                        size="xl"
                        color={fontColor}
                        entryId={componentData.sys.id}
                        fieldId="richHeader"
                      />
                    )}

                    <DocumentToReactRender
                      componentData={description}
                      size="xl"
                      color={fontColor}
                      entryId={componentData.sys.id}
                      fieldId="description"
                    />

                    {button?.fields && <ContentfulButton buttonData={button} />}
                  </Flex>
                </GridItem>
              </ContainerGrid>
            </Flex>
          </Flex>
        </Box>
      </div>

      <div className={styles['hero-banner-mobile-only']}>
        {/* Mobile Image */}
        <Box position="absolute" inset={0} zIndex={0}>
          <Image
            src={getBackgroundImageSrc(mobileImage)}
            alt="Hero Banner Tablet"
            className={styles['hero-tablet-mobile-only']}
            fill
            objectFit="cover"
            priority
          />
        </Box>
        <Flex
          justifyContent={{
            base: 'flex-end',
          }}
          alignItems="center"
          flexDirection="column"
          textAlign="left"
          minH={{
            base: 'max(calc(115vh - 0px), 528px)',
            md: 'max(calc(140vh - 60px), 58px)',
          }}
          pt={{
            base: '500px',
            md: '500px',
          }}
          mb={{ base: '120px', md: '0' }}
          position="absolute"
          inset={0}
          zIndex={1}
        >
          <Flex
            flexDirection="column"
            gap="40px"
            w="100%"
            bgColor={{
              base: bgColor,
              md: 'transparent',
            }}
            borderTopLeftRadius={{
              base: '100% 80px',
              md: 0,
            }}
            borderTopRightRadius={{
              base: '100% 80px',
              md: 0,
            }}
            py="40px"
          >
            <ContainerGrid width="100%">
              <GridItem
                colStart={{
                  base: 1,
                  md: textOnTheRightSide ? 6 : 1,
                }}
                colEnd={{ base: 13, md: textOnTheRightSide ? 13 : 8 }}
              >
                <Flex
                  flexDirection="column"
                  w={{ base: '100%' }}
                  textAlign={{
                    base: 'center',
                    md: textOnTheRightSide ? 'right' : 'left',
                  }}
                  alignItems={{
                    base: 'center',
                    md: textOnTheRightSide ? 'flex-end' : 'flex-start',
                  }}
                >
                  {richHeader && (
                    <DocumentToReactRender
                      componentData={richHeader}
                      size="xl"
                      color={fontColor}
                      entryId={componentData.sys.id}
                      fieldId="richHeader"
                    />
                  )}

                  <DocumentToReactRender
                    componentData={description}
                    size="xl"
                    color={fontColor}
                    entryId={componentData.sys.id}
                    fieldId="description"
                  />

                  {button?.fields && <ContentfulButton buttonData={button} />}
                </Flex>
              </GridItem>
            </ContainerGrid>
          </Flex>
        </Flex>
      </div>
    </>
  )
}
